// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ChartLegendSettings.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import ChartSettingsContainer from './Izenda.ChartSettingsContainer';
import { CHART_SETTINGS } from 'logic/data/Izenda.ReportPartChartConstants';
import ChartConfig from 'logic/data/Izenda.ChartDefaultConfig';
import { MAP_TYPES } from 'logic/data/Izenda.ReportPartMapConstants';
import UICONSTANTS from 'logic/data/Izenda.UIConstants';
import localizer from 'share/localization/Izenda.Localizer';
import Font from 'share/components/controls/Izenda.Font';
import Thickness from 'share/components/controls/Izenda.Thinkness';
import DebouncedInput from 'share/components/controls/Izenda.DebouncedInput';
import { get, getOr, values, omit, merge, find } from 'lodash/fp';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';

const WIDGET = {
  Font,
  Thickness,
  DebouncedInput
};
const { FONT_STYLE } = CHART_SETTINGS;
const UNUSED_LEGEND_CHART_PROPS = ['title', 'titleFont', 'layout'];

class Title {
  @observable text;
  @observable style;

  constructor(detail) {
    this.update(detail);
  }

  @action
  update({ text, style }) {
    this.text = text;
    this.style = style;
  }
}

class Legend {
  @observable visible;
  @observable borderVisible;
  @observable horizontalAlign;
  @observable verticalAlign;
  @observable borderColor;
  @observable borderWidth;
  @observable font;
  @observable title;
  @observable layout;
  @observable backgroundColor;

  constructor(detail) {
    this.title = new Title({});
    this.update(detail);
  }

  @action
  update({
    visible,
    title,
    horizontalAlign,
    verticalAlign,
    layout,
    backgroundColor,
    borderVisible,
    borderColor,
    borderWidth,
    font
  }) {
    this.visible = visible;
    this.title.update(title);
    this.horizontalAlign = horizontalAlign;
    this.verticalAlign = verticalAlign;
    this.layout = layout;
    this.backgroundColor = backgroundColor;
    this.borderVisible = borderVisible;
    this.borderColor = borderColor;
    this.borderWidth = borderWidth;
    this.font = font;
  }
}

@observer
export default class ChartLegendSettings extends Component {
  constructor(props: any) {
    super(props);

    this.init();
  }

  getValue = () => {
    const {
      visible,
      borderVisible,
      horizontalAlign,
      verticalAlign,
      borderColor,
      borderWidth,
      font,
      title,
      layout,
      backgroundColor
    } = this.model;
    const nonWidth = 0;
    return {
      'izLegend.visibility': visible,
      'izLegend.horizontalAlign': horizontalAlign,
      'izLegend.verticalAlign': verticalAlign,
      'izLegend.borderColor': borderColor,
      'izLegend.borderWidth': visible && borderVisible ? borderWidth : nonWidth,
      'izLegend.fontStyling': font,
      'izLegend.title': title,
      'izLegend.layout': layout,
      'izLegend.backgroundColor': backgroundColor
    };
  };

  init() {
    const value = this.props.value || {};
    const visible = value['izLegend.visibility'] || false;
    const borderColor = value['izLegend.borderColor'];
    const borderWidth = value['izLegend.borderWidth'];
    const title = value['izLegend.title'];
    this.model = new Legend({
      visible: visible,
      title: {
        text: get('text', title),
        style: getOr({}, 'style', title)
      },
      horizontalAlign: value['izLegend.horizontalAlign'] || ChartConfig.DefaultConfig.LEGEND_HORIZONTAL_ALIGNMENT,
      verticalAlign: value['izLegend.verticalAlign'] || ChartConfig.DefaultConfig.LEGEND_VERTICAL_ALIGNMENT,
      layout: value['izLegend.layout'] || ChartConfig.DefaultConfig.LEGEND_LAYOUT_ALIGNMENT,
      backgroundColor: value['izLegend.backgroundColor'],
      borderVisible: visible && !!borderWidth,
      borderColor: borderColor || CHART_SETTINGS.DEFAULT_VALUE.BLACK_COLOR,
      borderWidth: borderWidth || CHART_SETTINGS.DEFAULT_VALUE.WIDTH,
      font: value['izLegend.fontStyling']
    });
  }

  getConfigurationDef = () => {
    const that = this;
    const {
      visible,
      borderVisible,
      title,
      title: { text, style },
      horizontalAlign,
      verticalAlign,
      layout,
      borderColor,
      borderWidth,
      font
    } = this.model;

    const getSchema = () => ({
      schemaKey: 'LEGEND_SETTINGS',
      fields: {
        visible: {
          title: localizer.getText('COMMON_LABEL_VISIBLE'),
          type: 'checkbox',
          fnValue: 'getVisible',
          controlClassName: 'u_controlHeight'
        },
        title: {
          title: localizer.getText('COMMON_LABEL_TITLE'),
          component: 'DebouncedInput',
          fnValue: 'getTitle',
          props: {
            disabled: !visible,
            onChangeValue: 'handleChangeTitle'
          }
        },
        titleFont: {
          title: localizer.getText('FONT_SETTINGS_LABEL_FONT'),
          component: 'Font',
          fnValue: 'getTitleFont',
          titleClassName: 'izenda-Legend-TitleFont',
          props: {
            disabled: !visible
          },
          staticProps: {
            showBackgroundColor: false
          }
        },
        horizontalAlignment: {
          title: localizer.getText('REPORTDESIGNER_LABEL_HORIZONTAL_ALIGNMENT'),
          type: 'select',
          fnValue: 'getHorizontalAlignment',
          props: {
            options: ChartConfig.getOptions('HORIZONTAL_ALIGN'),
            defaultValue: ChartConfig.DefaultConfig.LEGEND_HORIZONTAL_ALIGNMENT,
            disabled: !visible
          }
        },
        verticalAlignment: {
          title: localizer.getText('REPORTDESIGNER_LABEL_VERTICAL_ALIGNMENT'),
          type: 'select',
          fnValue: 'getVerticalAlignment',
          props: {
            options: ChartConfig.getOptions('VERTICAL_ALIGN'),
            defaultValue: ChartConfig.DefaultConfig.LEGEND_VERTICAL_ALIGNMENT,
            disabled: !visible
          }
        },
        layout: {
          title: 'Layout',
          type: 'select',
          fnValue: 'getLayout',
          props: {
            options: ChartConfig.getOptions('LAYOUT_ALIGN'),
            defaultValue: ChartConfig.DefaultConfig.LEGEND_LAYOUT_ALIGNMENT,
            disabled: !visible
          }
        },
        border: {
          title: localizer.getText('REPORTDESIGNER_LABEL_BORDER'),
          type: 'checkbox',
          fnValue: 'getBorderVisible',
          controlClassName: 'u_controlHeight',
          props: {
            checked: borderVisible,
            disabled: !visible
          }
        },
        color: {
          title: localizer.getText('COMMON_LABEL_COLOR'),
          titleClassName: 'izenda-Legend-BorderColor',
          type: 'color',
          fnValue: 'getColor',
          props: {
            disabled: !borderVisible || !visible
          }
        },
        thickness: {
          title: localizer.getText('BORDER_SETTING_LABEL_THICKNESS'),
          component: 'Thickness',
          titleClassName: 'izenda-Legend-Thickness',
          fnValue: 'getThickness',
          props: {
            disabled: !borderVisible || !visible
          }
        },
        font: {
          title: localizer.getText('FONT_SETTINGS_LABEL_FONT'),
          component: 'Font',
          fnValue: 'getFont',
          props: {
            disabled: !visible
          }
        }
      }
    });

    const constructFont = font => {
      font = font || {};
      return {
        style: {
          fontBold: font.fontWeight === FONT_STYLE.BOLD,
          fontItalic: font.fontStyle === FONT_STYLE.ITALIC,
          fontUnderline: font.textDecoration === FONT_STYLE.UNDERLINE
        },
        format: {
          fontFamily: font.fontFamily || UICONSTANTS.defaultFontFamily,
          fontSize: font.fontSize || UICONSTANTS.defaultFontSize
        },
        color: font.color || CHART_SETTINGS.DEFAULT_VALUE.BLACK_COLOR,
        backgroundColor: font.backgroundColor || UICONSTANTS.backgroundColor
      };
    };

    const chartType = this.props.chartType;
    const schema = getSchema();
    // Remove unused props if we're configuring for chart
    if (!find(type => chartType === type, values(MAP_TYPES))) {
      schema.fields = omit(UNUSED_LEGEND_CHART_PROPS, schema.fields);
    }

    return {
      definedSchema: schema,
      widgets: WIDGET,
      source: {},
      mappingSource: {
        getVisible: () => visible,
        getTitle: () => text,
        getTitleFont: () => constructFont(style),
        getHorizontalAlignment: () => horizontalAlign,
        getVerticalAlignment: () => verticalAlign,
        getLayout: () => layout,
        getBorderVisible: () => borderVisible,
        getColor: () => borderColor,
        getThickness: () => borderWidth,
        getFont: () => constructFont(font)
      },
      mappingProps: {
        handleChangeTitle: value => {
          const newTitle = merge(title, { text: value });
          that.model.title = newTitle;
        }
      },
      onChangeValue: this.onChangeValue
    };
  };

  render() {
    const {
      visible,
      borderVisible,
      title,
      horizontalAlign,
      verticalAlign,
      layout,
      borderColor,
      borderWidth,
      font,
      backgroundColor
    } = this.model;

    const chartOptions = {
      commonOptions: {
        'izLegend.visibility': visible,
        'izLegend.horizontalAlign': horizontalAlign,
        'izLegend.verticalAlign': verticalAlign,
        'izLegend.borderColor': visible && borderVisible ? borderColor : undefined,
        'izLegend.borderWidth': visible && borderVisible ? borderWidth : undefined,
        'izLegend.fontStyling': font,
        'izLegend.title': title,
        'izLegend.layout': layout,
        'izLegend.backgroundColor': backgroundColor
      }
    };

    return (
      <ChartSettingsContainer
        className='legend-settings'
        type={this.props.reportPartType}
        configurationDef={this.getConfigurationDef()}
        chartOptions={chartOptions}
        allChartProps={this.props.allChartProps}
      />
    );
  }

  onChangeValue = (schemaData: any, changedKey: string, changedKeyPath: any, changedValue: any) => {
    const groupData = changedKeyPath && changedKeyPath.length ? get(changedKeyPath, schemaData) : schemaData;
    const fieldData = groupData[changedKey];

    fieldData && (fieldData.value = changedValue);

    // Don't update Map every times text in title changed
    if (changedKey === 'title') {
      return;
    }

    function constructLegendStyle(font) {
      font = font || {};
      const style = font.style || {};
      const format = font.format || {};
      const fontSize = format.fontSize;

      return {
        fontWeight: style.fontBold ? FONT_STYLE.BOLD : '',
        fontStyle: style.fontItalic ? FONT_STYLE.ITALIC : '',
        textDecoration: style.fontUnderline ? FONT_STYLE.UNDERLINE : '',
        fontSize: `${fontSize}px`,
        fontFamily: format.fontFamily,
        color: font.color,
        backgroundColor: font.backgroundColor
      };
    }

    const font = schemaData.font.value;
    const visible = schemaData.visible.value;
    const borderVisible = visible && schemaData.border.value;
    const title = {
      text: get('title.value', schemaData),
      style: constructLegendStyle(get('titleFont.value', schemaData))
    };
    this.model.update({
      visible: visible,
      title: title,
      horizontalAlign: schemaData.horizontalAlignment.value,
      verticalAlign: schemaData.verticalAlignment.value,
      layout: get('layout.value', schemaData),
      backgroundColor: get('backgroundColor', font),
      borderVisible: borderVisible,
      borderColor: schemaData.color.value,
      borderWidth: schemaData.thickness.value,
      font: constructLegendStyle(font)
    });
  };
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartLegendSettings.jsx